import React, { createContext, FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { getUserDataWithUsername, IUserProps } from '../common/data/userDummyData';
import authTokenSet from '../API/authTokenSet';

export interface IAuthContextProps {
	user: string;
	setUser: React.Dispatch<React.SetStateAction<string>>;
	userData: Partial<IUserProps>;
	token: string;
	setToken: React.Dispatch<React.SetStateAction<string>>;
	logout: () => void;
}

const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthContextProviderProps {
	children: ReactNode;
}

export const AuthContextProvider: FC<IAuthContextProviderProps> = ({ children }) => {
	const [user, setUser] = useState<string>(localStorage.getItem('userInfo') || '');
	const [token, setToken] = useState<string>(localStorage.getItem('authToken') || '');
	const [userData, setUserData] = useState<Partial<IUserProps>>({});

	/** ------------------------------
	 *  Sync USER in localStorage
	 * ------------------------------ */
	useEffect(() => {
		if (user) localStorage.setItem('userInfo', user);
		else localStorage.removeItem('userInfo');
	}, [user]);

	/** ------------------------------
	 *  Sync TOKEN in localStorage + axios header
	 * ------------------------------ */
	useEffect(() => {
		if (token) {
			localStorage.setItem('authToken', token);
			authTokenSet(token);
		} else {
			localStorage.removeItem('authToken');
			authTokenSet(''); // clear token header
		}
	}, [token]);

	/** ------------------------------
	 *  Load userData when username changes
	 * ------------------------------ */
	useEffect(() => {
		if (!user) {
			setUserData({});
			return;
		}

		// 🔹 If user is JSON (API login)
		if (user.startsWith('{')) {
			try {
				const parsed = JSON.parse(user);
				setUserData(parsed);
				return;
			} catch {
				setUserData({});
				return;
			}
		}

		// 🔹 If user is plain username (dummy login)
		const dummyUser = getUserDataWithUsername(user);
		setUserData(dummyUser || {});
	}, [user]);


	/** ------------------------------
	 *  LOGOUT FUNCTION
	 * ------------------------------ */
	const logout = () => {
		setUser('');
		setToken('');
		setUserData({});
		localStorage.clear();
		// localStorage.removeItem('username');
		// localStorage.removeItem('authToken');

		authTokenSet('');

		// recommended logout redirect
		window.location.href = '/auth-pages/login';
	};

	/** ------------------------------
	 *  Context Value
	 * ------------------------------ */
	const value = useMemo(
		() => ({
			user,
			setUser,
			userData,
			token,
			setToken,
			logout,
		}),
		[user, userData, token],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
